import { View } from 'backbone.marionette';
import _ from 'lodash';
import Backbone from 'backbone';
import Ladda from 'ladda';
import Syphon from 'backbone.syphon';

import downloadDocument from '@/util/downloadDocument';
import FileUploadModel from '@/fileupload/FileUploadModel';
import OrderFileUploadTmpl from '@/fileupload/OrderFileUploadTmpl.hbs';
import showErrorMessage from '@/util/showErrorMessage';
import showSuccessMessage from '@/util/ShowSuccessMessage';
import ValidFileTypeUtil from '@/util/ValidFileTypeUtil';
import { validateForm } from '@/util/ParsleyUtils';
import DocumentTypes from '@/enums/DocumentTypes';
import DocumentDownloadTypes from '@/enums/DocumentDownloadTypes';
import Generate7600BFundingDocument from '../../../order/services/Generate7600BFundingDocument';

import AttachmentsCollectionView from '../AttachmentsCollectionView';
import FileUploadView from '../FileUploadView';

import tmplFundingAttachmentView from './FundingAttachmentsView.hbs';

const FundingAttachmentView = View.extend({
  template: tmplFundingAttachmentView,

  className: 'funding-attachment',

  ui: {
    fileName: '#fileName',
    attachmentLabel: '.attachment-label',
    fundingAttachmentsList: '.funding-attachments-list',
    fundingTypeSelect: '#fundingTypeSelect',
    fundingUploadBtnContainer: '.btn-upload-funding-container',
    uploadFundingBtn: '.upload-funding-btn',
    generate7600BBtn: '.generate-7600b-btn',
    type7600BContainer: '#type7600BContainer',
    fundingInput: '.funding-input',
    gInvoicingOrderNumber: 'input[name="gInvoicingOrderNumber"]',
    fundingAmount: 'input[name="fundingAmount"]',
    type7600BForm: '#type7600BForm',
    fundingAttachmentsForm: '#fundingAttachmentsForm',
    err7600bRequired: '.err-7600b-required',
    errFundingRequired: '.err-funding-required'
  },

  regions: {
    attachmentLabel: '@ui.attachmentLabel',
    fundingAttachmentsList: '@ui.fundingAttachmentsList',
  },

  events: {
    'click @ui.fileName': 'onClickFileName',
    'change @ui.fundingTypeSelect': 'onChangeFundingTypeSelect',
    'change #file-upload-input': 'onFileSelect',
    'click @ui.generate7600BBtn': 'onClickGenerate7600B',
    'click @ui.uploadFundingBtn': 'onClickFundingUploadBtn'
  },

  childEvents: {
    'file:deleted': 'onFileDelete',
  },

  childViewEvents: {
    'file:deleted': 'onFileDelete'
  },

  collectionEvents: {
    'file:deleted': 'onFileDelete'
  },

  initialize(options) {
    const attachments = this.model.get('attachments');
    const isReadOnly = this.model.get('isReadOnly');
    const fundingDocumentTypes = this.model.get('fundingDocumentTypes');
    const fundingAttachmentMapping = this.mapDocs(fundingDocumentTypes, attachments, options, isReadOnly);
    this.model.set('fundingAttachmentMapping', fundingAttachmentMapping);
  },

  mapDocs(documentTypes, attachments, options, isReadOnly) {
    const documentMapping = _.map(documentTypes, (documentType) => {
      const { documentTypeDescription, documentTypeId, attachmentId, defaultFileName } = documentType;
      const isRequired = documentType?.isRequired === '1';
      const attachment = _.find(attachments, (attachment) => attachment.documentTypeId === documentTypeId) || null;

      if (attachment !== null) {
        return {
          label: {
            text: documentTypeDescription,
            fileName: documentType.defaultFileName,
            isDocument: false, //removes download link from label
            downloadType: DocumentDownloadTypes.TEMPLATE,
            attachmentId
          },
          attachment: {
            isReadOnly,
            attachmentName: attachment?.fileName,
            attachmentId: attachment?.id,
            fileUploadTmpl: OrderFileUploadTmpl,
            pathToFileId: 'html.head.script.Envelope.Body.UploadOrderAttachmentResponse.AttachmentID.__text',
            defaultFileName,
            validator: ValidFileTypeUtil,
            metadata: {
              attachmentId: attachment?.id,
              fileName: attachment?.fileName,
              orderId: options?.orderId,
              documentTypeId
            }
          },
          isRequired,
          isReadOnly
        };
      }
    });

    // return array of uploaded funding attachments
    return _.reject(documentMapping, _.isUndefined);
  },

  onRender() {
    this.collection = new Backbone.Collection(this.model.get('fundingAttachmentMapping'));
    this.fundingAttachmentsCollectionView = new AttachmentsCollectionView({
      collection: this.collection
    });
    this.showChildView('fundingAttachmentsList', this.fundingAttachmentsCollectionView);

    const isReadOnly = this.model.get('isReadOnly');
    this.ui.fundingInput.prop('disabled', isReadOnly);

    this.handleHideShowFundingFields(this.model.get('fundingTypeID'));

    this.ui.err7600bRequired.hide();
    this.ui.errFundingRequired.hide();
  },

  onChangeFundingTypeSelect(e) {
    if (e) e.preventDefault();

    this.model.set('fundingTypeID', this.ui.fundingTypeSelect.val());
    this.handleHideShowFundingFields(this.model.get('fundingTypeID'));
  },

  handleHideShowFundingFields(fundingType) {
    //set hide/show on sections and disable any hidden fields to prevent them from being serialized on save.
    const isReadOnly = this.model.get('isReadOnly');

    if (fundingType === DocumentTypes['7600B']) {
      this.ui.type7600BContainer.show();
      this.ui.gInvoicingOrderNumber.prop('disabled', isReadOnly);
      this.ui.fundingAmount.prop('disabled', isReadOnly);
    } else {
      this.ui.type7600BContainer.hide();
      this.ui.gInvoicingOrderNumber.prop('disabled', true);
      this.ui.fundingAmount.prop('disabled', true);
    }

    this.ui.fundingUploadBtnContainer.toggle([DocumentTypes.MIPR, DocumentTypes.GPC_FORM].includes(fundingType));
    this.showAttachmentLabel(fundingType);
  },

  showAttachmentLabel(documentTypeId) {
    const fundingDocumentTypes = this.model.get('fundingDocumentTypes');
    const documentType = fundingDocumentTypes.find((dt) => dt.documentTypeId === documentTypeId);

    if (!documentType) {
      this.getRegion('attachmentLabel').empty();
      return;
    }

    const fileUploadModel = new FileUploadModel({
      fileName: documentType.defaultFileName,
      label: documentType.documentTypeDescription,
      hideFileIcon: true,
      alignRight: true,
      isReadOnly: true,
      attachmentId: documentType.attachmentId,
      downloadType: DocumentDownloadTypes.TEMPLATE
    });

    const collection = new Backbone.Collection([fileUploadModel]);

    const fileUploadView = new FileUploadView({
      isReadOnly: true,
      isRequired: false,
      displayAsterisk: false,
      allowMultipleFiles: false,
      uploadClass: 'btn btn-outline-primary btn-sm',
      collection
    });

    this.showChildView('attachmentLabel', fileUploadView);
  },

  onClickFileName(e) {
    e.preventDefault();
    const attachment = this.model.get('attachment');
    try {
      downloadDocument({
        attachmentId: this.model.get('newAttachmentId') ? this.model.get('newAttachmentId') : attachment.id,
        fileName: this.model.get('newFileName') ? this.model.get('newFileName') : attachment.fileName,
        isTemplate: 0
      });
    } catch {
      this.showDownloadError();
    }
  },

  onClickFundingUploadBtn(e) {
    e.preventDefault();
    this.$('#file-upload-input').trigger('click');
  },

  async onFileSelect(e) {
    e.preventDefault();
    const files = e.target.files || e.originalEvent.dataTransfer.files;
    const file = files[0];
    const spinner = Ladda.create(this.ui.uploadFundingBtn[0]);
    spinner.start();
    await this.uploadFunding(file);
    spinner.stop();
    this.trigger('file:uploaded');
  },

  async uploadFunding(file) {
    if (!ValidFileTypeUtil(file)) {
      return;
    }
    const documentTypeId = this.model.get('fundingTypeID');
    const orderId = this.model.get('orderId');

    const fileUploadModel = new FileUploadModel({
      file1: file,
      attachmentName: file.name,
      fileName: file.name,
      showError: this.showUploadError,
      isReadOnly: this.model.get('isReadOnly'),
      metadata: {
        orderId,
        documentTypeId
      },
      pathToFileId: 'html.head.script.Envelope.Body.UploadOrderAttachmentResponse.AttachmentID.__text',
      tmplMethodXML: OrderFileUploadTmpl
    });

    try {
      const documentType = this.model.get('displayFundingTypes').find(type => type.id === documentTypeId);

      if(this.hasExistingDocument(documentTypeId)) {
        this.showDuplicateDocumentTypeError(documentType.name)
        throw new Error();
      }

      await fileUploadModel.upload();

      //update funding attachment collection with the newly uploaded file.
      const uploadedAttachment = {
        isReadOnly: this.model.get('isReadOnly'),
        label: {
          text: documentType.name,
          fileName: file.name,
          isDocument: false,
          downloadType: DocumentDownloadTypes.TEMPLATE,
          attachmentId: fileUploadModel.get('attachmentId')
        },
        attachment: {
          isReadOnly: this.model.get('isReadOnly'),
          attachmentName: fileUploadModel.get('fileName'),
          attachmentId: fileUploadModel.get('attachmentId'),
          fileUploadTmpl: fileUploadModel.get('tmplMethodXML'),
          pathToFileId: fileUploadModel.get('pathToFileId'),
          validator: ValidFileTypeUtil,
          metadata: fileUploadModel.get('metadata')
        }
      };
      this.fundingAttachmentsCollectionView.collection.add(uploadedAttachment);
      this.showUploadSuccess();
    } catch{
      this.showUploadError();
    } finally {
      this.validate();
    }
  },

  hasExistingDocument(documentTypeId) {
    return this.fundingAttachmentsCollectionView.collection.some((model) => {
      return model.attributes.attachment.metadata.documentTypeId === documentTypeId;
    });
  },

  async onFileDelete(e) {
    const deletedDocument = _.filter(this.fundingAttachmentsCollectionView.collection.models, (model) => {
      return model.attributes.label.attachmentId === e.attributes.label.attachmentId;
    });
    this.fundingAttachmentsCollectionView.collection.remove(deletedDocument);
    this.validate();
  },

  async onClickGenerate7600B(e) {
    e.preventDefault();
    console.log('generate')
    const spinner = Ladda.create(this.ui.generate7600BBtn[0]);

    try {
      const isValid = await this.validate();
      if(!isValid) return;

      spinner.start();
    
      const fundingData = Syphon.serialize(this.ui.type7600BForm);

      const orderId = this.model.get('orderId');
      const documentTypeId = this.model.get('fundingTypeID');
      const documentType = this.model.get('displayFundingTypes').find(type => type.id === documentTypeId);

      if(this.hasExistingDocument(documentTypeId)) {
        this.showDuplicateDocumentTypeError(documentType.name)
        throw new Error();
      }

      const response = await Generate7600BFundingDocument(orderId, fundingData.gInvoicingOrderNumber, fundingData.fundingAmount);

      const uploadedAttachment = {
        isReadOnly: this.model.get('isReadOnly'),
        label: {
          text: documentType.name,
          fileName: response.fileName,
          isDocument: false,
          downloadType: DocumentDownloadTypes.TEMPLATE,
          attachmentId: response.attachmentId
        },
        attachment: {
          isReadOnly: this.model.get('isReadOnly'),
          attachmentName: response.fileName,
          attachmentId: response.attachmentId,
          pathToFileId: 'html.head.script.Envelope.Body.UploadOrderAttachmentResponse.AttachmentID.__text',
          tmplMethodXML: OrderFileUploadTmpl,
          validator: ValidFileTypeUtil,
          metadata: {
            orderId,
            documentTypeId
          },
        }
      };
      this.fundingAttachmentsCollectionView.collection.add(uploadedAttachment);

      this.showGenerate7600BSuccess();
    } catch {
      this.showGenerate7600BError();
    } finally {
      spinner.stop();
    }
  },

  async validate(calledOnSubmit) {
    const documentTypeId = this.model.get('fundingTypeID');
    this.ui.err7600bRequired.hide();
    this.ui.errFundingRequired.hide();

    if (documentTypeId === DocumentTypes['7600B']) {
      //if the funding type is set to 7600B, validate the g-invoicing number and funding amount fields.
      try {
        await validateForm(this.ui.type7600BForm);
        if(calledOnSubmit && !this.hasExistingDocument(DocumentTypes['7600B'])) {
          this.ui.err7600bRequired.show();
          throw new Error();
        }
        this.trigger('no:errors');
        return true;
      } catch {
        this.trigger('has:errors');
        return false;
      }
    } else if (this.fundingAttachmentsCollectionView.collection.length < 1) {
      this.ui.errFundingRequired.show();
      this.trigger('has:errors');
      return false;
    } else {
      try {
        this.trigger('no:errors');
        return true;
      } catch {
        this.trigger('has:errors');
        return false;
      }
    }
  },

  showDuplicateDocumentTypeError(documentTypeName) {
    showErrorMessage({
      errorCode: 'duplicateDocType',
      errorTitle: 'Duplicate Document Type',
      errorMessage: `A ${documentTypeName} has already been uploaded for this order. If you need to upload a new version of this document, please delete the existing document before attempting to upload the new version.`
    });
  },

  showUploadError() {
    showErrorMessage({
      errorCode: 'fileUploadError',
      errorTitle: 'File Upload Error',
      errorMessage: 'An error occurred while uploading the funding document. Please contact an administrator.'
    });
  },

  showUploadSuccess() {
    showSuccessMessage({
      code: 'fileUploadSuccess',
      title: 'File Upload Successfully',
      message: 'The funding document has been successfully uploaded.',
      hideAfter: 5
    });
  },

  showGenerate7600BError() {
    showErrorMessage({
      errorCode: 'generate7600BError',
      errorTitle: 'Unable to Generate 7600B',
      errorMessage:
        'An error occurred while attempting to generate the 7600B document. Please contact an administrator.'
    });
  },

  showGenerate7600BSuccess() {
    showSuccessMessage({
      code: 'generate7600BSuccess',
      title: '7600B Document Generation Successful',
      message: 'The 7600B document has been generated and attached to the order.',
      hideAfter: 5
    });
  },

  showDownloadError() {
    showErrorMessage({
      errorCode: 'fileDownloadError',
      errorTitle: 'File Download Error',
      errorMessage:
        'An error occurred while attempting to download the funding attachment. Please contact an administrator.'
    });
  }
});

export default FundingAttachmentView;
